import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { tradeAreaService } from 'features/Map'
import {
	fetchOtherRetailers,
	saveOtherRetailers,
	removeOtherRetailer,
	scanOtherRetailers,
	clearScan,
	anchorKey,
} from 'slices/otherRetailerSlice'

/**
 * Other Retailers across the candidate sites of one evaluation.
 *
 * Linear: MYD-486.
 *
 * ---------------------------------------------------------------------------
 * Why this is a second hook rather than `useOtherRetailers` again
 * ---------------------------------------------------------------------------
 *
 * `useOtherRetailers` serves one asset: one anchor, one ring limit, one list.
 * That is the whole shape of the Locations and Projects maps, where the screen
 * is about a single place.
 *
 * The evaluation Sites tab is about CHOOSING between places, so the question it
 * exists to answer is "who is near site A versus site B". That needs:
 *
 *   - a saved list per candidate site, not one list for the evaluation
 *   - a map showing every site's retailers at once, so the comparison is visible
 *   - each site's retailers clipped to THAT site's rings, because ring
 *     visibility here is per-site (`RingVisibilityConfig`) rather than global
 *
 * Trying to express that through the single-anchor hook would have meant
 * calling it in a loop, which React hooks rules forbid outright.
 *
 * ---------------------------------------------------------------------------
 * Focused site is not the winner
 * ---------------------------------------------------------------------------
 *
 * The caller passes `focusedSiteId` for the sidebar and the scan dialog. That
 * is deliberately NOT `evaluation.selectedSite`, which is the WINNER behind the
 * trophy toggle and is usually unset while someone is still comparing. Scanning
 * would then have no anchor on exactly the screens where it is most useful.
 */

export interface SiteForRetailers {
	_id: string
	name?: string
	latitude?: number
	longitude?: number
}

export interface UseOtherRetailersForSitesArgs {
	/**
	 * PRECISE sites only. An approximate placement is a city or postal-code
	 * centroid, and `toPreciseSites` already excludes those from rings for the
	 * reason that applies doubly here: retail "within 1 mile" of a postal-code
	 * centroid is a measurement of nothing.
	 */
	sites: SiteForRetailers[]
	/** Per-site enabled radii, the same config `TradeAreaRings` consumes. */
	ringVisibility: Record<string, number[]>
	/** Which site the sidebar and the scan dialog act on. */
	focusedSiteId?: string | null
	enabled?: boolean
}

const ANCHOR_TYPE = 'EvaluationSite' as const

export function useOtherRetailersForSites({
	sites,
	ringVisibility,
	focusedSiteId,
	enabled = true,
}: UseOtherRetailersForSitesArgs) {
	const dispatch = useDispatch<any>()
	const [isScanOpen, setIsScanOpen] = useState(false)

	const { entities, byAnchor, isLoading } = useSelector((state: any) => state.otherRetailers)

	const siteIds = useMemo(() => sites.map(s => String(s._id)), [sites])
	// Join rather than depend on the array: callers rebuild `sites` on every
	// render, and an array identity in the dependency list would refetch every
	// retailer for every site on any unrelated state change.
	const siteIdKey = siteIds.join(',')

	useEffect(() => {
		if (!enabled || siteIds.length === 0) return
		for (const anchorId of siteIds) {
			dispatch(fetchOtherRetailers({ anchorType: ANCHOR_TYPE, anchorId }))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, enabled, siteIdKey])

	/**
	 * Each site's saved retailers, narrowed by that site's own rings.
	 *
	 * Rings narrow, they do not gate, so a site with no ring selected shows
	 * everything saved against it. A retailer whose distance could not be
	 * computed is kept rather than hidden: the server recomputes distance on
	 * read, so a null means the site lost its coordinates, and dropping the
	 * user's saved rows would look like data loss.
	 */
	const bySite = useMemo(() => {
		const out: Record<string, any[]> = {}
		for (const anchorId of siteIds) {
			const key = anchorKey({ anchorType: ANCHOR_TYPE, anchorId })
			const rows = (byAnchor[key] || []).map((id: string) => entities[id]).filter(Boolean)
			const limit = tradeAreaService.outermostRadius(ringVisibility[anchorId] || [])
			out[anchorId] =
				limit == null
					? rows
					: rows.filter((r: any) => r.distanceMiles == null || r.distanceMiles <= limit)
		}
		return out
	}, [siteIds, byAnchor, entities, ringVisibility])

	/**
	 * Every site's retailers on one map, deduplicated by provider id.
	 *
	 * The dedupe is the point. Two candidate sites a mile apart will both have
	 * the same Target in range, saved as two rows against two anchors. Drawn
	 * naively that is two identical pins stacked on one coordinate, which reads
	 * as a rendering bug. Keep the instance nearest to its own site, so the pin
	 * that survives is the one whose distance label is the most favourable
	 * reading for that retailer.
	 */
	const unionItems = useMemo(() => {
		const bestBySource = new Map<string, any>()
		for (const anchorId of siteIds) {
			for (const row of bySite[anchorId] || []) {
				const dedupeKey = row.sourceId ? `${row.source}/${row.sourceId}` : String(row._id)
				const existing = bestBySource.get(dedupeKey)
				if (!existing || (row.distanceMiles ?? Infinity) < (existing.distanceMiles ?? Infinity)) {
					bestBySource.set(dedupeKey, row)
				}
			}
		}
		return [...bestBySource.values()]
	}, [siteIds, bySite])

	const countsBySite = useMemo(() => {
		const out: Record<string, number> = {}
		for (const anchorId of siteIds) out[anchorId] = (bySite[anchorId] || []).length
		return out
	}, [siteIds, bySite])

	const focusedItems = useMemo(
		() => (focusedSiteId ? bySite[focusedSiteId] || [] : []),
		[focusedSiteId, bySite],
	)

	const focusedRingLimit = useMemo(
		() =>
			focusedSiteId
				? tradeAreaService.outermostRadius(ringVisibility[focusedSiteId] || [])
				: null,
		[focusedSiteId, ringVisibility],
	)

	/** A site can only be scanned when it is one of the precise ones passed in. */
	const canScanFocused = Boolean(enabled && focusedSiteId && siteIds.includes(focusedSiteId))

	const openScan = useCallback(() => setIsScanOpen(true), [])

	const closeScan = useCallback(() => {
		setIsScanOpen(false)
		dispatch(clearScan())
	}, [dispatch])

	const runScan = useCallback(
		(options: { radiusMiles?: number; categories?: string[]; namedChainsOnly?: boolean }) => {
			if (!canScanFocused) return
			return dispatch(
				scanOtherRetailers({
					anchorType: ANCHOR_TYPE,
					anchorId: focusedSiteId as string,
					...options,
				}),
			)
		},
		[dispatch, canScanFocused, focusedSiteId],
	)

	const saveSelection = useCallback(
		async (selected: any[]) => {
			if (!canScanFocused || selected.length === 0) return
			const result = await dispatch(
				saveOtherRetailers({
					anchorType: ANCHOR_TYPE,
					anchorId: focusedSiteId as string,
					items: selected,
				}),
			)
			if (!result.error) setIsScanOpen(false)
			return result
		},
		[dispatch, canScanFocused, focusedSiteId],
	)

	const removeItem = useCallback(
		(item: any) => {
			/**
			 * Accepts a raw row, a mapped `OtherRetailItem`, or a bare id.
			 *
			 * `OtherRetailPanel` hands back an `OtherRetailItem`, whose id field is
			 * `id` rather than `_id` because the panel is fed a mapped shape. An
			 * earlier version of this guard tested `_id` alone, so it was always
			 * undefined and every remove returned early in silence.
			 */
			const id = typeof item === 'string' ? item : (item?._id ?? item?.id)
			if (!id) return

			/**
			 * Find the anchor from state rather than asking the caller for it.
			 * The mapped panel item carries no `anchor`, and requiring callers to
			 * thread it through is how the id mismatch happened in the first
			 * place. The hook already knows which site every row belongs to.
			 */
			const owning = siteIds.find(anchorId =>
				(bySite[anchorId] || []).some((r: any) => String(r._id) === String(id)),
			)
			const anchorId = owning ?? focusedSiteId
			if (!anchorId) return

			return dispatch(removeOtherRetailer({ anchorType: ANCHOR_TYPE, anchorId, id }))
		},
		[dispatch, focusedSiteId, siteIds, bySite],
	)

	return {
		bySite,
		unionItems,
		countsBySite,
		focusedItems,
		focusedRingLimit,
		totalSaved: useMemo(
			() => siteIds.reduce((n, id) => n + (bySite[id]?.length || 0), 0),
			[siteIds, bySite],
		),
		isLoading: enabled ? isLoading : false,
		isScanOpen,
		openScan,
		closeScan,
		runScan,
		saveSelection,
		removeItem,
		canScanFocused,
		/** True when this surface has any precise site to hang retailers off. */
		isAvailable: Boolean(enabled && siteIds.length > 0),
	}
}

export default useOtherRetailersForSites
